import { plural } from '@rental/i18n'
import { cn } from '@rental/ui'
import { Delete } from 'lucide-react'
import { type ReactNode, type Ref, useEffect, useId, useState } from 'react'
import { useLocale } from '../i18n'

const TOUCH = '(any-pointer: coarse)'
const LENGTH = 6
const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const

/** Whether the screen can be touched; then a PIN has its own keypad and the system keyboard stays down. */
export function useTouchScreen(): boolean {
  const [touch, setTouch] = useState(() => window.matchMedia(TOUCH).matches)
  useEffect(() => {
    const query = window.matchMedia(TOUCH)
    const update = () => setTouch(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  return touch
}

/**
 * D9: six dots for a PIN's six digits, drawn under the field that holds them.
 * The field stays a real one, focusable and editable: a keyboard types into
 * it, and its focus is a ring round the next dot to fill (UI-2a: the owner's
 * mock-up has no box round the dots; styles.css). On a touch screen a keypad
 * types into the same value, through the same `onChange`, so the sixth digit
 * unlocks either way; the field then asks for no system keyboard.
 * While a PIN is on its way (`disabled`) only the keys stop: the field keeps
 * the focus. Before anyone is chosen (`waiting`) the field stops too, and
 * `describedBy` names what it waits for.
 */
export function PinEntry({
  label,
  value,
  onChange,
  disabled,
  waiting = false,
  describedBy,
  inputRef,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  disabled: boolean
  waiting?: boolean
  describedBy?: string
  inputRef: Ref<HTMLInputElement>
}) {
  const { locale, messages: m } = useLocale()
  const id = useId()
  const countId = useId()
  const touch = useTouchScreen()
  return (
    <div className="flex w-full flex-col items-center gap-3">
      <div className="pin-field relative">
        <div aria-hidden="true" className="flex gap-3 p-2">
          {Array.from({ length: LENGTH }, (_, index) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: six fixed places
              key={index}
              // The ring marks the next dot, and the last once all six are in, while they are
              // on their way: the field keeps the focus then, and had no ring (UI-2g).
              // `pin-filled`: in forced colours a filled dot's edge widens to fill it (styles.css).
              className={cn(
                'size-4 rounded-control border-2',
                index < value.length
                  ? 'pin-filled border-brand-strong bg-brand-strong'
                  : 'border-text-muted',
                index === Math.min(value.length, LENGTH - 1) && 'pin-next',
              )}
            />
          ))}
        </div>
        <label htmlFor={id} className="sr-only">
          {label}
        </label>
        <input
          ref={inputRef}
          id={id}
          type="password"
          inputMode={touch ? 'none' : 'numeric'}
          autoComplete="off"
          maxLength={LENGTH}
          value={value}
          disabled={waiting}
          aria-describedby={describedBy ? `${countId} ${describedBy}` : countId}
          onChange={(event) => onChange(event.target.value)}
          // Unseen over the dots, and taller than them: a finger's 44 px, within the gaps around.
          className="absolute inset-x-0 -inset-y-2 rounded-sm bg-transparent text-base text-transparent caret-transparent"
        />
        {/* The dots are drawn, not read: how many digits are in is said as it changes,
            a key of the keypad pressed included. Nothing while the field is empty. */}
        <p id={countId} aria-live="polite" className="sr-only">
          {value.length > 0 ? plural(locale, m.lock.digits, value.length) : ''}
        </p>
      </div>
      {touch ? (
        <fieldset className="w-full min-w-0">
          <legend className="sr-only">{m.lock.keypad}</legend>
          <div className="grid grid-cols-3 gap-2">
            {DIGITS.map((digit) => (
              <Key
                key={digit}
                disabled={disabled || waiting}
                describedBy={waiting ? describedBy : undefined}
                onPress={() => onChange(value + digit)}
              >
                {digit}
              </Key>
            ))}
            <span aria-hidden="true" />
            <Key
              disabled={disabled || waiting}
              describedBy={waiting ? describedBy : undefined}
              onPress={() => onChange(`${value}0`)}
            >
              0
            </Key>
            <Key
              disabled={disabled || waiting}
              describedBy={waiting ? describedBy : undefined}
              label={m.lock.deleteDigit}
              onPress={() => onChange(value.slice(0, -1))}
            >
              <Delete aria-hidden="true" className="size-6" strokeWidth={1.75} />
            </Key>
          </div>
        </fieldset>
      ) : null}
    </div>
  )
}

/** A key of the keypad. Waiting for a name, it says so, as the field and Sblocca do (UI-2g). */
function Key({
  children,
  label,
  disabled,
  describedBy,
  onPress,
}: {
  children: ReactNode
  label?: string
  disabled: boolean
  describedBy?: string
  onPress: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-describedby={describedBy}
      disabled={disabled}
      // The focus stays in the field, where a keyboard would type next.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onPress}
      // UI-2a: soft keys, as in the owner's mock-up. A key is known by its digit, as on a
      // phone; its edge is the faint rule (DESIGN.md, "PinEntry"). Its focus and its press are not.
      className="figures flex min-h-12 touch-manipulation items-center justify-center rounded-card border border-rule bg-surface-pane-strong text-xl font-medium text-brand-strong transition-colors hover:bg-surface-well disabled:cursor-not-allowed disabled:opacity-60 motion-safe:active:scale-[var(--press-scale)]"
    >
      {children}
    </button>
  )
}
